import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Loading from '../../components/loading';
import ErrorMessage from '../../components/errorMessage';
import SuccessMessage from '../../components/successMessage';
import { useUsers } from './hooks/useUsers';
import { useDeleteUser } from './hooks/useDeleteUser';
import UserModal from './userModal';
import RoleAccessInfo from './roleAccessInfo';
import { roleLabel } from './roles';

export default function UsersAndAccess() {
  const navigate = useNavigate();
  const { users, isLoading, error, refetch } = useUsers();
  const { remove } = useDeleteUser();

  const [isDeleting, setDeleting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [deleteModal, setDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    if (error) {
      setShowErrorMessage(true);
      setErrorMessage('Não foi possível carregar os usuários.');
    }
  }, [error]);

  const handleNewClick = () => {
    setSelectedUser(null);
    setModalOpen(true);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleSaved = () => {
    setSuccessMessage('Usuário salvo com sucesso.');
    setShowSuccessMessage(true);
    refetch();
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    setDeleting(true);
    remove(userToDelete.id)
      .then((returnFromApi) => {
        setSuccessMessage(String(returnFromApi.data ?? 'Usuário excluído com sucesso.'));
        setShowSuccessMessage(true);
        setDeleteModal(false);
        setUserToDelete(null);
        refetch();
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(
          String(returnFromApi.response?.data ?? 'Não foi possível excluir o usuário.'),
        );
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.response?.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => setDeleting(false));
  };

  return (
    <>
      <Loading isToHidden={isLoading === false && isDeleting === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <RoleAccessInfo />

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Usuários e acessos
            </Typography>
          }
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewClick}>
              Novo Usuário
            </Button>
          }
        />
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Login</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Perfis de acesso</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 && !isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Nenhum usuário cadastrado.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.login}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(user.roles ?? []).map((role) => (
                            <Chip key={role} label={roleLabel(role)} size="small" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleEditClick(user)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(user)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <UserModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaved}
        user={selectedUser}
      />

      <Dialog open={deleteModal} onClose={() => setDeleteModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir usuário</DialogTitle>
        <DialogContent dividers>
          <Typography>
            O usuário <b>{userToDelete?.name}</b> perderá o acesso ao sistema. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModal(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ height: 8 }} />
    </>
  );
}
